/**
 * KOA Music Player Component
 * Handles audio playback functionality for the KOA project
 * Provides methods to play, pause, skip tracks and manage audio state
 */

class MusicPlayer {
  /**
   * Initialize the Music Player component
   * Sets up audio elements, controls and event listeners
   */
  constructor() {
    this.musicList = [{ title: 'Permita-se', file: '/audio/music1.mp3' }];

    this.currentTrack = 0;
    this.audio = new Audio();
    this.isPlaying = false;
    this.isInitialized = false;
    this.hasEventListeners = false; // Track if event listeners are already set

    // DOM elements
    this.playPauseBtn = null;
    this.prevBtn = null;
    this.nextBtn = null;
    this.autoplayWarning = null;

    // Don't auto-initialize in constructor - let app.js control it
    console.log(
      'MusicPlayer: Constructor completed, waiting for explicit init() call'
    );
  }

  /**
   * Initialize music player component
   * Sets up DOM references, event listeners and loads first track
   */
  init() {
    console.log('MusicPlayer: Starting initialization...');

    // Reset initialization state
    this.isInitialized = false;
    this.hasEventListeners = false;

    // Wait for DOM if needed
    if (document.readyState === 'loading') {
      console.log('MusicPlayer: DOM still loading, waiting...');
      document.addEventListener('DOMContentLoaded', () => this.init());
      return;
    }

    console.log('MusicPlayer: DOM ready, setting up components...');
    this.setupDOMElements();

    if (this.isInitialized) {
      this.setupEventListeners();
      this.setupAudioEventListeners();
      this.loadTrack(this.currentTrack);
      this.attemptAutoplay();
      console.log('MusicPlayer: Full initialization completed');
    } else {
      console.error(
        'MusicPlayer: Failed to initialize - DOM elements not found'
      );
      // Retry after a short delay
      setTimeout(() => {
        console.log('MusicPlayer: Retrying initialization...');
        this.init();
      }, 500);
    }
  }

  /**
   * Set up DOM element references
   * Gets references to player controls and warning elements
   */
  setupDOMElements() {
    console.log('MusicPlayer: Looking for DOM elements...');

    this.playPauseBtn = document.getElementById('play-pause-btn');
    this.prevBtn = document.getElementById('prev-btn');
    this.nextBtn = document.getElementById('next-btn');
    this.autoplayWarning = document.getElementById('autoplay-warning');

    console.log('MusicPlayer: Elements found:', {
      playPauseBtn: !!this.playPauseBtn,
      prevBtn: !!this.prevBtn,
      nextBtn: !!this.nextBtn,
      autoplayWarning: !!this.autoplayWarning,
    });

    if (!this.playPauseBtn || !this.prevBtn || !this.nextBtn) {
      console.warn('MusicPlayer: DOM elements not found');
      this.isInitialized = false;
      return;
    }

    this.isInitialized = true;
    console.log('MusicPlayer: DOM elements initialized successfully');
  }

  /**
   * Set up event listeners for player controls
   * Handles play/pause, previous and next track functionality
   */
  setupEventListeners() {
    if (!this.isInitialized) {
      console.error(
        'MusicPlayer: Cannot setup event listeners - not initialized'
      );
      return;
    }

    // Prevent duplicate event listeners
    if (this.hasEventListeners) {
      console.log('MusicPlayer: Event listeners already set up, skipping...');
      return;
    }

    console.log('MusicPlayer: Setting up event listeners...');

    // Remove any existing event listeners first
    this.removeEventListeners();

    // Play/Pause button event listener
    this.playPauseClickHandler = e => {
      console.log('MusicPlayer: Play/pause button clicked');
      console.log('MusicPlayer: Current state before click:', {
        isPlaying: this.isPlaying,
        audioSrc: this.audio.src,
        audioPaused: this.audio.paused,
        audioCurrentTime: this.audio.currentTime,
        audioReadyState: this.audio.readyState,
      });

      e.preventDefault();
      e.stopPropagation();

      if (this.isPlaying) {
        console.log('MusicPlayer: Attempting to pause...');
        this.pauseTrack();
      } else {
        console.log('MusicPlayer: Attempting to play...');
        this.playTrack();
      }
    };

    // Previous track button event listener
    this.prevClickHandler = e => {
      console.log('MusicPlayer: Previous button clicked');
      e.preventDefault();
      e.stopPropagation();
      this.previousTrack();
    };

    // Next track button event listener
    this.nextClickHandler = e => {
      console.log('MusicPlayer: Next button clicked');
      e.preventDefault();
      e.stopPropagation();
      this.nextTrack();
    };

    // Add event listeners
    this.playPauseBtn.addEventListener('click', this.playPauseClickHandler);
    this.prevBtn.addEventListener('click', this.prevClickHandler);
    this.nextBtn.addEventListener('click', this.nextClickHandler);

    this.hasEventListeners = true;
    console.log('MusicPlayer: Event listeners setup completed');
  }

  /**
   * Remove event listeners to prevent duplicates
   */
  removeEventListeners() {
    if (this.playPauseBtn && this.playPauseClickHandler) {
      this.playPauseBtn.removeEventListener(
        'click',
        this.playPauseClickHandler
      );
    }
    if (this.prevBtn && this.prevClickHandler) {
      this.prevBtn.removeEventListener('click', this.prevClickHandler);
    }
    if (this.nextBtn && this.nextClickHandler) {
      this.nextBtn.removeEventListener('click', this.nextClickHandler);
    }
    this.hasEventListeners = false;
    console.log('MusicPlayer: Event listeners removed');
  }

  /**
   * Set up audio element event listeners
   * Handles audio loading, playback and error events
   */
  setupAudioEventListeners() {
    // Track ended - auto play next track
    this.audio.addEventListener('ended', () => {
      this.nextTrack();
    });

    // Audio loading events
    this.audio.addEventListener('loadstart', () => {});

    this.audio.addEventListener('canplay', () => {});

    this.audio.addEventListener('error', e => {
      this.handleAudioError(e);
    });

    // Playback state events
    this.audio.addEventListener('play', () => {
      this.isPlaying = true;
      this.updatePlayButton();
    });

    this.audio.addEventListener('pause', () => {
      this.isPlaying = false;
      this.updatePlayButton();
    });
  }

  /**
   * Load a specific track by index
   * @param {number} index - The track index to load
   */
  loadTrack(index) {
    if (index < 0 || index >= this.musicList.length) {
      return;
    }

    const track = this.musicList[index];
    this.audio.src = track.file;
    this.currentTrack = index;
  }

  /**
   * Play the current track
   * Handles play promise and error states
   */
  playTrack() {
    console.log('MusicPlayer: playTrack() called');
    console.log('MusicPlayer: Audio state:', {
      src: this.audio.src,
      paused: this.audio.paused,
      currentTime: this.audio.currentTime,
      readyState: this.audio.readyState,
    });

    const currentTrackInfo = this.musicList[this.currentTrack];
    console.log('MusicPlayer: Current track info:', currentTrackInfo);

    const playPromise = this.audio.play();

    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          console.log('MusicPlayer: Play promise resolved successfully');
          this.isPlaying = true;
          this.updatePlayButton();
          this.hideAutoplayWarning();
        })
        .catch(error => {
          console.error('MusicPlayer: Play promise rejected:', error);
          this.isPlaying = false;
          this.updatePlayButton();
          this.showAutoplayWarning();
        });
    } else {
      console.warn('MusicPlayer: Play promise is undefined');
    }
  }

  /**
   * Pause the current track
   */
  pauseTrack() {
    console.log('MusicPlayer: pauseTrack() called');
    console.log('MusicPlayer: Audio state before pause:', {
      paused: this.audio.paused,
      currentTime: this.audio.currentTime,
    });

    this.audio.pause();
    this.isPlaying = false;
    this.updatePlayButton();

    console.log('MusicPlayer: Track paused, isPlaying set to false');
  }

  /**
   * Skip to previous track
   * Loops to last track if at beginning
   */
  previousTrack() {
    this.currentTrack =
      (this.currentTrack - 1 + this.musicList.length) % this.musicList.length;
    this.loadTrack(this.currentTrack);
    if (this.isPlaying) {
      this.playTrack();
    }
  }

  /**
   * Skip to next track
   * Loops to first track if at end
   */
  nextTrack() {
    this.currentTrack = (this.currentTrack + 1) % this.musicList.length;
    this.loadTrack(this.currentTrack);
    if (this.isPlaying) {
      this.playTrack();
    }
  }

  /**
   * Update play/pause button icon based on current state
   */
  updatePlayButton() {
    if (!this.playPauseBtn) return;

    const icon = this.isPlaying ? 'ph-pause' : 'ph-play';
    this.playPauseBtn.innerHTML = `<i class="ph ${icon}"></i>`;
  }

  /**
   * Show autoplay warning message
   */
  showAutoplayWarning() {
    if (this.autoplayWarning) {
      this.autoplayWarning.style.display = 'block';
    }
  }

  /**
   * Hide autoplay warning message
   */
  hideAutoplayWarning() {
    if (this.autoplayWarning) {
      this.autoplayWarning.style.display = 'none';
    }
  }

  /**
   * Handle audio errors
   * @param {Event} error - The error event
   */
  handleAudioError(error) {
    this.isPlaying = false;
    this.updatePlayButton();

    // Could implement retry logic or user notification here
  }

  /**
   * Attempt autoplay on page load
   * Handles browser autoplay policies gracefully
   */
  attemptAutoplay() {
    const playPromise = this.audio.play();

    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          this.isPlaying = true;
          this.updatePlayButton();
          this.hideAutoplayWarning();
          console.log('Autoplay successful');
        })
        .catch(error => {
          this.isPlaying = false;
          this.updatePlayButton();
          console.warn('Autoplay failed:', error.message);
          this.showAutoplayWarning();
          this.setupFirstClickHandler();
        });
    }
  }

  /**
   * Set up first click handler to enable audio after user interaction
   * Required for browsers that block autoplay
   */
  setupFirstClickHandler() {
    const tryPlay = () => {
      this.playTrack();
      this.hideAutoplayWarning();
      window.removeEventListener('click', tryPlay);
    };

    window.addEventListener('click', tryPlay);
  }

  /**
   * Get current track information
   * @returns {Object} Current track object with title and file
   */
  getCurrentTrack() {
    return this.musicList[this.currentTrack];
  }

  /**
   * Get player state information
   * @returns {Object} Object containing player state
   */
  getState() {
    return {
      isPlaying: this.isPlaying,
      currentTrack: this.currentTrack,
      trackInfo: this.getCurrentTrack(),
      duration: this.audio.duration,
      currentTime: this.audio.currentTime,
    };
  }

  /**
   * Set volume level
   * @param {number} volume - Volume level between 0 and 1
   */
  setVolume(volume) {
    if (volume >= 0 && volume <= 1) {
      this.audio.volume = volume;
    }
  }
}

// Create and export music player instance
const musicPlayer = new MusicPlayer();

export default MusicPlayer;
export { musicPlayer };
